import React, { useLayoutEffect, useRef, useState } from "react";
import contactBorder from "@assets/images/borders/contactBorder.webp";
import type { IAddressItem } from "@src/types/AppTypes";
import AddAddress from "@components/Address/AddAddress";
import useOnclickOutside from "react-cool-onclickoutside";
import DeleteConfirmation from "../Downloads/DeleteConfirmation";

const AddressItem: React.FC<IAddressItem> = ({
  item,
  updateAddresses,
  index,
  deleteAddress,
  addresses,
}) => {
  const [addressModal, setAddressModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  // for close edit address modal on clicling outside
  const editAddressModalRef = useRef<HTMLDivElement>(null);
  useOnclickOutside(
    () => {
      document.getElementById("address-edit-form")?.classList.add("form-shake");
    },
    {
      refs: [editAddressModalRef],
      ignoreClass: [
        "swal2-container",
        "no-shake-animation",
        "delete-modal-content",
      ],
    }
  );

  // for close address delete modal on outside click
  const deleteConfirmationModalRef = useRef<HTMLDivElement>(null);
  useOnclickOutside(
    () => {
      document.getElementById("Delete Address?")?.classList.add("form-shake");
    },
    {
      refs: [deleteConfirmationModalRef],
      ignoreClass: ["swal2-container", "delete-modal-content"],
    }
  );
  // for close edit address delete modal on outside click
  const deleteEditAddressConfirmationModalRef = useRef<HTMLDivElement>(null);

  const [scrollPosition, setScrollPosition] = useState(0);
  useLayoutEffect(() => {
    if (deleteModal || addressModal) {
      const currentScroll = window.scrollY;
      setScrollPosition(currentScroll);
      document.body.style.top = `-${currentScroll}px`;
      document.body.classList.add("!fixed", "overflow-hidden", "w-full");
    } else {
      document.body.classList.remove("!fixed", "overflow-hidden", "w-full");
      document.body.style.top = "";
      window.scrollTo(0, scrollPosition);
    }

    return () => {
      document.body.classList.remove("!fixed", "overflow-hidden", "w-full");
      document.body.style.top = "";
    };
  }, [deleteModal, addressModal]);

  return (
    <div className="relative py-5 px-8 max-md:p-4 max-md:border max-md:border-[#89D9EE] max-md:rounded-xl">
      <img
        className="absolute top-0 left-0 size-full max-md:hidden"
        src={contactBorder}
        alt="border-image"
      />
      <div className="relative">
        <div className="flex items-start justify-between">
          <h6 className="text-2xl max-md:text-xl text-[#89D9EE] metrics-overflow">
            {item.is_primary ? "Primary Address" : "Alternate Address"}
          </h6>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                setAddressModal(true);
              }}
              className="flex items-center text-xl duration-150 hover:scale-95"
              aria-label="edit-button"
            >
              <i className="ri-edit-line"></i>
            </button>
            <button
              onClick={async (e) => {
                e.preventDefault();
                setDeleteModal(true); // for open delete confirmation modal
              }}
              className="flex items-center text-base duration-150 hover:scale-95"
              aria-label="delete-buttton"
            >
              {isDeleting ? (
                <i className="ri-loop-right-line btn-loader"></i>
              ) : (
                <i className="ri-delete-bin-line text-2xl"></i>
              )}
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1 mt-3">
          <h6 className="text-primary text-xl break-words line-clamp-3">
            {item.street_name}
          </h6>
          <h6 className="text-primary text-xl break-words line-clamp-3">
            {item.street_name2}
          </h6>
          <h6 className="text-primary text-xl break-words me-14">
            {item.city + ", " + item.state + " " + item.zip_code}
          </h6>
        </div>
      </div>

      {deleteModal && (
        <DeleteConfirmation
          title="Delete Address?"
          headTitle="Delete Confirmation."
          setModal={async (val) => {
            if (val) {
              setIsDeleting(true);
              await deleteAddress(item.id!); // for delete address
              setIsDeleting(false);
            }
            setDeleteModal(false); // close modal after click on confirm delete
          }}
          ref={deleteConfirmationModalRef}
          isPassWay={true}
        />
      )}
      {addressModal && (
        <AddAddress
          address={item}
          closeModal={setAddressModal}
          updateAddresses={updateAddresses}
          index={index}
          isEdit={true}
          title="Edit"
          ref={editAddressModalRef}
          deteteRef={deleteEditAddressConfirmationModalRef}
          id="address-edit-form"
          addresses={addresses}
        />
      )}
    </div>
  );
};

export default AddressItem;
